/**
 * Dummy stand-in for the Targetin pull API: hardcoded data on a timer, in the
 * shape the real calls will have. Delete once those exist.
 */

/** One dataset Targetin offers. */
export interface TargetinDataset {
  id: string;
  name: string;
}

interface DummyTargetinDataset extends TargetinDataset {
  /** Left out to stay loading forever, so Cancel always has a target. */
  durationMs?: number;
  totalRows: number;
  fails?: boolean;
}

/**
 * One per state worth exercising: one succeeds, one fails so Retry has a target,
 * and one never settles so Cancel does. The first two land close together, so
 * their notifications overlap.
 */
const DUMMY_DATASETS: DummyTargetinDataset[] = [
  {
    id: "targetin-indomie-q2-2026-v1",
    name: "Indomie – Q2 2026 v1",
    durationMs: 1_500,
    totalRows: 200
  },
  {
    id: "targetin-indomie-q2-2026-v1-2",
    name: "Indomie – Q2 2026 v1.2",
    durationMs: 2_700,
    totalRows: 0,
    fails: true
  },
  {
    id: "targetin-indomie-q2-2026-v1-3",
    name: "Indomie – Q2 2026 v1.3",
    totalRows: 0
  }
];

/** Retrieves the dataset list. Async: until it answers, nothing is known. */
export const fetchTargetinDatasets = (): Promise<TargetinDataset[]> =>
  new Promise((resolve) =>
    setTimeout(
      () => resolve(DUMMY_DATASETS.map(({ id, name }) => ({ id, name }))),
      1_200
    )
  );

/** Distinguishes a cancelled import from one that failed on its own. */
export class TargetinImportCancelledError extends Error {
  constructor() {
    super("Import cancelled");
    this.name = "TargetinImportCancelledError";
  }
}

/** Retrieves one dataset's metadata. Rejects for the dataset marked as failing. */
export const fetchTargetinDatasetMetadata = (
  datasetId: string,
  signal?: AbortSignal
): Promise<{ totalRows: number }> =>
  new Promise((resolve, reject) => {
    const dataset = DUMMY_DATASETS.find(({ id }) => id === datasetId);

    if (!dataset) {
      reject(new Error(`Unknown Targetin dataset: ${datasetId}`));
      return;
    }

    if (signal?.aborted) {
      reject(new TargetinImportCancelledError());
      return;
    }

    const cancel = () => reject(new TargetinImportCancelledError());

    // Stays pending, leaving its row loading until cancelled.
    if (dataset.durationMs === undefined) {
      signal?.addEventListener("abort", cancel, { once: true });
      return;
    }

    const timer = setTimeout(() => {
      if (dataset.fails) {
        reject(new Error(`Failed to import ${dataset.name} from Targetin`));
        return;
      }
      resolve({ totalRows: dataset.totalRows });
    }, dataset.durationMs);

    // Registered after the timer so it can stop it. Aborting once settled is
    // harmless: rejecting a settled promise does nothing.
    signal?.addEventListener(
      "abort",
      () => {
        clearTimeout(timer);
        cancel();
      },
      { once: true }
    );
  });
